import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { useToast } from '../hooks/use-toast';
import { useLocale } from '../context/LocaleContext';
import { 
  Upload, FileText, Loader2, CheckCircle, XCircle, 
  File, Trash2, Download, Eye, RefreshCw
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';

const FileProcessing = () => {
  const { toast } = useToast();
  const { t } = useLocale();
  const [uploading, setUploading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [instruction, setInstruction] = useState('Summarize this document and extract key points');
  const [selectedJob, setSelectedJob] = useState(null);

  const onDrop = useCallback(async (acceptedFiles) => {
    for (const file of acceptedFiles) {
      await uploadFile(file);
    }
  }, [instruction]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'text/csv': ['.csv'],
      'application/json': ['.json']
    },
    maxSize: 50 * 1024 * 1024 // 50MB
  });

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('instruction', instruction);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/files/upload`,
        {
          method: 'POST',
          credentials: 'include',
          body: formData
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        toast({
          title: '📁 File Uploaded',
          description: `Processing started for ${file.name}`
        });
        loadJobs();
      } else {
        throw new Error(data.detail || 'Upload failed');
      }
    } catch (error) {
      toast({
        title: 'Upload Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const loadJobs = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/files/jobs`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        setJobs(data.jobs || []);
      }
    } catch (error) {
      console.error('Failed to load jobs:', error);
    }
  };

  const checkJobStatus = async (jobId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/files/jobs/${jobId}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        setJobs(prev => prev.map(j => j.id === jobId ? data.job : j));
        if (data.job.status === 'completed') {
          toast({ title: '✅ Processing Complete', description: `${data.job.filename} is ready` });
        }
      }
    } catch (error) {
      console.error('Failed to check job:', error);
    }
  };

  const deleteJob = async (jobId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/files/jobs/${jobId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (response.ok) {
        setJobs(prev => prev.filter(j => j.id !== jobId));
        toast({ title: 'Deleted', description: 'Job removed' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  React.useEffect(() => {
    loadJobs();
    const interval = setInterval(() => {
      jobs.filter(j => j.status === 'processing' || j.status === 'queued')
        .forEach(j => checkJobStatus(j.id));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed': return <XCircle className="h-5 w-5 text-red-500" />;
      case 'processing': return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      default: return <FileText className="h-5 w-5 text-gray-400" />;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" /> File Processing
          </CardTitle>
          <CardDescription>
            Upload PDF, DOCX, TXT, CSV, or JSON files for AI-powered analysis
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Instruction Input */}
          <div>
            <label className="text-sm font-medium">Analysis Instruction</label>
            <input
              type="text"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder="What would you like to know about this document?"
              className="w-full mt-1 p-2 border rounded-lg"
            />
          </div>

          {/* Drop Zone */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
              ${isDragActive ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-gray-300 hover:border-purple-400'}
              ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
          >
            <input {...getInputProps()} />
            {uploading ? (
              <div className="flex flex-col items-center">
                <Loader2 className="h-12 w-12 text-purple-500 animate-spin mb-4" />
                <p className="text-gray-500">Uploading...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <File className="h-12 w-12 text-purple-500 mb-4" />
                {isDragActive ? (
                  <p className="text-purple-600 font-medium">Drop files here...</p>
                ) : (
                  <>
                    <p className="text-gray-600 font-medium">Drag & drop files here</p>
                    <p className="text-sm text-gray-400 mt-1">or click to select files</p>
                    <p className="text-xs text-gray-400 mt-2">Supports PDF, DOCX, TXT, CSV, JSON (max 50MB)</p>
                  </>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Jobs List */}
      {jobs.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Processing Jobs</CardTitle>
              <Button variant="outline" size="sm" onClick={loadJobs}>
                <RefreshCw className="h-4 w-4 mr-1" /> Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {jobs.map((job) => (
                <div 
                  key={job.id} 
                  className="p-4 border rounded-lg flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <div className="flex items-center gap-3">
                    {getStatusIcon(job.status)}
                    <div>
                      <p className="font-medium">{job.filename}</p>
                      <p className="text-xs text-gray-500">
                        {job.status === 'processing' && `${job.progress}% complete`}
                        {job.status === 'completed' && 'Ready to view'}
                        {job.status === 'failed' && job.error}
                        {job.status === 'queued' && 'Waiting to process...'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {job.status === 'processing' && (
                      <Progress value={job.progress} className="w-24" />
                    )}
                    {job.status === 'completed' && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedJob(job)}
                      >
                        <Eye className="h-4 w-4 mr-1" /> View
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => deleteJob(job.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Result Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <CardHeader className="border-b">
              <div className="flex justify-between items-center">
                <CardTitle>{selectedJob.filename}</CardTitle>
                <Button variant="ghost" onClick={() => setSelectedJob(null)}>✕</Button>
              </div>
            </CardHeader>
            <CardContent className="p-4 overflow-y-auto max-h-[60vh]">
              <div className="prose dark:prose-invert max-w-none">
                {selectedJob.result || 'No result available'}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default FileProcessing;
